import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FiClock, FiLock, FiUpload, FiUser } from "react-icons/fi";
import { usePageHeader } from "../hooks/usePageHeader";
import { LoadingSpinner } from "../Components/LoadingSpinner";
import { UserAvatar } from "../Components/UserAvatar";
import { usePermissions } from "../Auth/PermissionsContext";
import { UserActivityJournal } from "../Components/Account/UserActivityJournal";
import {
	changeMyPassword,
	getMyActivity,
	getMyProfile,
	getUserActivity,
	getUserProfile,
	uploadMyAvatar,
	type AccountProfile,
	type ActivityFilter,
	type UserProfile
} from "../Components/Account/accountClient";

/**
 * « Mon compte » (self) and read-only profiles of other users (/account/:id). The self view exposes
 * the profile card + avatar upload, a security section (password change : the form is always shown but
 * disabled with a note on directories that can't change passwords, e.g. the local dev directory), and
 * the personal activity journal (server-paged, filterable by action / level / period / text). Another
 * user's view is gated by feature:profile:read: the profile card always, activity only when the caller
 * ALSO holds feature:user-activity:read; NEVER a security section.
 */

const Panel: React.FC<{ title: string; icon?: React.ReactNode; children: React.ReactNode; className?: string; bodyClassName?: string }> = ({ title, icon, children, className, bodyClassName }) => (
	<section className={`flex min-h-0 flex-col rounded-2xl border border-slate-200 bg-white shadow-sm ${className ?? ""}`}>
		<header className="flex shrink-0 items-center gap-2 border-b border-slate-100 px-4 py-3">
			{icon}
			<h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">{title}</h3>
		</header>
		<div className={`flex min-h-0 flex-col ${bodyClassName ?? ""}`}>{children}</div>
	</section>
);

const Account: React.FC = () => {
	const { id } = useParams<{ id?: string }>();
	const isSelf = !id;
	const { can } = usePermissions();
	const canSeeOthersActivity = can("feature:user-activity:read");
	// Another user's profile requires feature:profile:read; the caller's own /account is always allowed.
	const canSeeProfile = isSelf || can("feature:profile:read");

	usePageHeader({ title: isSelf ? "Mon compte" : "Profil utilisateur" });

	const [selfProfile, setSelfProfile] = useState<AccountProfile | null>(null);
	const [otherProfile, setOtherProfile] = useState<UserProfile | null>(null);
	const [error, setError] = useState<string | null>(null);

	// Password form
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [pwBusy, setPwBusy] = useState(false);
	const [pwMessage, setPwMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);

	const [avatarBusy, setAvatarBusy] = useState(false);
	const fileRef = useRef<HTMLInputElement>(null);

	const loadProfile = useCallback(async () => {
		setError(null);
		try {
			if (isSelf) {
				setSelfProfile(await getMyProfile());
			} else {
				setOtherProfile(await getUserProfile(id!));
			}
		} catch (e: any) {
			setError(e?.response?.data?.message ?? e?.message ?? "Impossible de charger le profil.");
		}
	}, [id, isSelf]);

	// Le journal (paginé + filtré) charge lui-même ses pages ; ici on lie seulement l'utilisateur visé.
	const fetchActivity = useCallback(
		(page: number, pageSize: number, filter: ActivityFilter) => (isSelf ? getMyActivity(page, pageSize, filter) : getUserActivity(id!, page, pageSize, filter)),
		[id, isSelf]
	);

	useEffect(() => {
		setSelfProfile(null);
		setOtherProfile(null);
		if (!canSeeProfile) return; // don't even fetch another user's profile without the permission
		loadProfile();
	}, [loadProfile, canSeeProfile]);

	const profile = isSelf ? selfProfile : otherProfile;
	const displayName = profile?.displayName ?? "";
	const email = profile?.email ?? "";
	const roles = profile?.roles ?? [];

	const handleAvatar = useCallback(
		async (file: File) => {
			setAvatarBusy(true);
			setError(null);
			try {
				const { avatarKey } = await uploadMyAvatar(file);
				setSelfProfile((prev) => (prev ? { ...prev, avatarKey } : prev));
			} catch (e: any) {
				setError(e?.response?.data?.message ?? e?.message ?? "L'envoi de l'image a échoué.");
			} finally {
				setAvatarBusy(false);
			}
		},
		[]
	);

	const handleChangePassword = useCallback(async () => {
		setPwMessage(null);
		if (newPassword !== confirmPassword) {
			setPwMessage({ tone: "error", text: "La confirmation ne correspond pas au nouveau mot de passe." });
			return;
		}
		setPwBusy(true);
		try {
			const result = await changeMyPassword(currentPassword, newPassword);
			const ok = result.status === "changed" || result.status === "email-sent";
			setPwMessage({ tone: ok ? "ok" : "error", text: result.message });
			if (ok) {
				setCurrentPassword("");
				setNewPassword("");
				setConfirmPassword("");
			}
		} catch (e: any) {
			setPwMessage({ tone: "error", text: e?.response?.data?.message ?? "Le changement de mot de passe a échoué." });
		} finally {
			setPwBusy(false);
		}
	}, [currentPassword, newPassword, confirmPassword]);

	if (!canSeeProfile) {
		return (
			<div className="min-h-full w-full bg-slate-50 px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
				<div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
					<p className="text-sm font-semibold text-slate-700">Accès refusé</p>
					<p className="mt-1 text-sm text-slate-500">Vous n'avez pas l'autorisation de consulter le profil des autres utilisateurs.</p>
				</div>
			</div>
		);
	}

	if (!profile) {
		return (
			<div className="flex items-center justify-center py-16">
				{error ? <p className="text-sm text-rose-600">{error}</p> : <LoadingSpinner size="lg" />}
			</div>
		);
	}

	const canChangePassword = !!selfProfile?.canChangePassword;

	return (
		<div className="flex min-h-full w-full flex-col gap-4 bg-slate-50 px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
			{error && <div className="shrink-0 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">{error}</div>}

			<Panel title={isSelf ? "Mon profil" : "Profil"} icon={<FiUser className="text-slate-400" />}>
				<div className="flex items-center gap-4 p-4">
					<UserAvatar
						name={displayName}
						id={id ?? profile.id ?? email}
						avatarKey={profile.avatarKey}
						pictureUrl={profile.pictureFallback}
						sizeClass="h-[72px] w-[72px]"
						textClass="text-2xl"
					/>
					<div className="min-w-0 flex-1">
						<p className="truncate text-lg font-semibold text-slate-800">{displayName || email}</p>
						<p className="truncate text-sm text-slate-500">{email}</p>
						<div className="mt-1 flex flex-wrap gap-1">
							{roles.map((r) => (
								<span key={r} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">{r}</span>
							))}
						</div>
					</div>
					{isSelf && (
						<div>
							<input
								ref={fileRef}
								type="file"
								accept="image/jpeg,image/png,image/webp"
								className="hidden"
								onChange={(e) => {
									const f = e.target.files?.[0];
									if (f) handleAvatar(f);
									e.target.value = "";
								}}
							/>
							<button
								type="button"
								onClick={() => fileRef.current?.click()}
								disabled={avatarBusy}
								className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
							>
								{avatarBusy ? <LoadingSpinner size="sm" inline /> : <FiUpload className="h-3.5 w-3.5" />}
								Photo de profil
							</button>
						</div>
					)}
				</div>
			</Panel>

			{/* NO `grid-cols-1` here on purpose. A second Tailwind build is loaded after the admin's own
			    (the renderer stylesheet the preview needs), and it redefines plain `.grid-cols-1`. Media
			    queries add no specificity, so that later plain rule BEAT `xl:grid-cols-2` and this grid
			    was stuck at one column at every width : which is why « Sécurité » took the full width.
			    An undecorated `grid` already lays out in one column, so dropping the class lets the xl
			    variant win with nothing to fight. items-start (not auto-rows-fr) then keeps « Sécurité »
			    at the height of its three password fields instead of the activity feed's.
			    On ANOTHER user's profile there is no « Sécurité » panel (never shown for others), so the
			    two-column split would leave the right half empty: the activity panel takes the full width. */}
			<div className={`grid min-h-0 flex-1 items-start gap-4 ${isSelf ? "xl:grid-cols-2" : ""}`}>
				{isSelf && (
					<Panel title="Sécurité" icon={<FiLock className="text-slate-400" />} bodyClassName="overflow-auto">
						<div className="space-y-3 p-4">
							<p className="text-sm text-slate-600">
								Modifiez votre mot de passe. Votre mot de passe actuel est demandé pour confirmer votre identité.
							</p>
							{!canChangePassword && (
								<div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
									Le changement de mot de passe est disponible en production (comptes Auth0). Il est désactivé sur cet environnement.
								</div>
							)}
							{pwMessage && (
								<div className={`rounded-lg px-3 py-2 text-sm ${pwMessage.tone === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
									{pwMessage.text}
								</div>
							)}
							<input
								type="password"
								autoComplete="current-password"
								placeholder="Mot de passe actuel"
								disabled={!canChangePassword}
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
								value={currentPassword}
								onChange={(e) => setCurrentPassword(e.target.value)}
							/>
							<input
								type="password"
								autoComplete="new-password"
								placeholder="Nouveau mot de passe"
								disabled={!canChangePassword}
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
							/>
							<input
								type="password"
								autoComplete="new-password"
								placeholder="Confirmer le nouveau mot de passe"
								disabled={!canChangePassword}
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
							/>
							<button
								type="button"
								onClick={handleChangePassword}
								disabled={!canChangePassword || pwBusy || !currentPassword || !newPassword || !confirmPassword}
								className="inline-flex items-center gap-1.5 rounded-lg bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white hover:bg-[#004a87] disabled:opacity-50"
							>
								{pwBusy && <LoadingSpinner size="sm" inline />} Changer le mot de passe
							</button>
						</div>
					</Panel>
				)}

				{/* The feed used to inherit a bounded height from auto-rows-fr, which is what let its list
				    scroll internally. On natural height it needs its own ceiling, or it grows forever. */}
				<Panel
					title={isSelf ? "Mon activité" : "Activité"}
					icon={<FiClock className="text-slate-400" />}
					className="max-h-[70vh]"
					bodyClassName="flex-1"
				>
					<UserActivityJournal fetchPage={fetchActivity} allowed={isSelf || canSeeOthersActivity} />
				</Panel>
			</div>
		</div>
	);
};

export default Account;
